import { Link, useLocation } from 'react-router-dom';
import Breadcrumb from '../../components/feature/Breadcrumb';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../hooks/useSEO';

const TroubleshootingPage = () => {
  const location = useLocation();
  const isEnglish = location.pathname.startsWith('/en');

  const TEXT = isEnglish ? {
    title: 'Troubleshooting',
    description: 'Solutions for common 3D printing issues',
    keywords: '3D printer,troubleshooting,warping,stringing,layer adhesion,solutions',
    breadcrumbHome: 'Home',
    breadcrumbTitle: 'Troubleshooting',
    pageTitle: 'Troubleshooting',
    pageSubtitle: 'Solutions for common issues encountered in practice',
    warping: 'Warping',
    warpingDesc: 'Edges of print lifting up',
    warpingCauses: ['Insufficient bed temperature', 'Excessive cooling', 'Poor adhesion'],
    warpingSolutions: ['Increase bed temperature', 'Adjust fan speed', 'Use brim'],
    stringing: 'Stringing',
    stringingDesc: 'Thin strings of filament during travel moves',
    stringingCauses: ['Insufficient retraction', 'Temperature too high', 'Slow travel speed'],
    stringSolutions: ['Adjust retraction', 'Lower temperature', 'Increase travel speed'],
    delamination: 'Layer Adhesion',
    delaminationDesc: 'Poor bonding between layers',
    delaminationCauses: ['Insufficient temperature', 'Excessive cooling', 'Layer height too high'],
    delaminationSolutions: ['Increase temperature', 'Adjust fan', 'Reduce layer height'],
    bleeding: 'Sagging / Overhang',
    bleedingDesc: 'Drooping in unsupported areas',
    bleedingCauses: ['Temperature too high', 'Insufficient cooling', 'Speed too fast'],
    bleedingSolutions: ['Adjust temperature', 'Increase fan', 'Adjust speed'],
    accuracy: 'Dimensional Accuracy',
    accuracyDesc: 'Difference between design and actual dimensions',
    accuracyCauses: ['Incorrect flow rate', 'Loose belts', 'Temperature fluctuation'],
    accuracySolutions: ['Adjust flow rate', 'Tighten belts', 'Stabilize temperature'],
    support: 'Support Removal',
    supportDesc: 'Difficulty removing support material',
    supportCauses: ['Support density too high', 'Insufficient contact distance', 'Material choice'],
    supportSolutions: ['Adjust density', 'Adjust distance', 'Use soluble material'],
    severityHigh: 'Critical',
    severityMedium: 'Moderate',
    severityLow: 'Minor',
    causesLabel: 'Main Causes',
    solutionsLabel: 'Solutions',
    detailsLink: '▶ View Details',
    notesTitle: 'About Troubleshooting',
    note1: '• Content based on actual experience with ELEGOO Centauri Carbon',
    note2: '• Results may vary depending on environment and equipment',
    note3: '• Click each item to see detailed solution steps',
    note4: '• Severity indicates impact on print quality'
  } : {
    title: 'トラブル対策',
    description: '3Dプリンターの反り、糸引き、層間剥離、滲み、寸法精度不良などのトラブル原因と対策方法。実際の運用で遭遇した問題の解決記録を掲載。',
    keywords: '3Dプリンター,トラブル,反り,糸引き,層間剥離,ワーピング,ストリンギング,対策',
    breadcrumbHome: 'ホーム',
    breadcrumbTitle: 'トラブル対策',
    pageTitle: 'トラブル対策',
    pageSubtitle: '実際に遭遇したトラブルと解決方法の記録',
    warping: '反り（ワーピング）',
    warpingDesc: 'プリント物の端が浮き上がる現象',
    warpingCauses: ['ベッド温度不足', '冷却速度過多', '接着不良'],
    warpingSolutions: ['ベッド温度上昇', 'ファン速度調整', 'ブリム使用'],
    stringing: '糸引き（ストリンギング）',
    stringingDesc: 'ノズル移動時に細い糸状の樹脂が残る',
    stringingCauses: ['リトラクション不足', '温度過高', '移動速度遅い'],
    stringSolutions: ['リトラクション調整', '温度下降', '移動速度向上'],
    delamination: '層間剥離',
    delaminationDesc: '積層した層同士の接着が不十分',
    delaminationCauses: ['温度不足', '冷却過多', '層高過大'],
    delaminationSolutions: ['温度上昇', 'ファン調整', '層高調整'],
    bleeding: '滲み・オーバーハング',
    bleedingDesc: 'サポートなし部分の垂れ下がり',
    bleedingCauses: ['温度過高', '冷却不足', '速度過速'],
    bleedingSolutions: ['温度調整', 'ファン強化', '速度調整'],
    accuracy: '寸法精度不良',
    accuracyDesc: '設計値と実測値の差異',
    accuracyCauses: ['フロー率不適', 'ベルト緩み', '温度変動'],
    accuracySolutions: ['フロー率調整', 'ベルト調整', '温度安定化'],
    support: 'サポート除去困難',
    supportDesc: 'サポート材の除去が困難',
    supportCauses: ['サポート密度過高', '接触距離不足', '材料選択'],
    supportSolutions: ['密度調整', '距離調整', '水溶性材料'],
    severityHigh: '重要',
    severityMedium: '中程度',
    severityLow: '軽微',
    causesLabel: '主な原因',
    solutionsLabel: '対策方法',
    detailsLink: '▶ 詳細を見る',
    notesTitle: 'トラブル対策について',
    note1: '• 記載内容は ELEGOO Centauri Carbon での実体験に基づきます',
    note2: '• 環境や機材により効果は異なる場合があります',
    note3: '• 各項目をクリックすると詳細な対策手順が確認できます',
    note4: '• 重要度は造形品質への影響度を示しています'
  };

  useSEO({
    title: TEXT.title,
    description: TEXT.description,
    keywords: TEXT.keywords,
    canonicalPath: isEnglish ? '/en/troubleshooting' : '/troubleshooting'
  });

  const prefix = isEnglish ? '/en' : '';

  const breadcrumbItems = [
    { label: TEXT.breadcrumbHome, path: prefix || '/' },
    { label: TEXT.breadcrumbTitle }
  ];

  const sideMenuItems = [
    { label: isEnglish ? 'All' : 'すべて', path: `${prefix}/troubleshooting`, icon: 'ri-tools-line' },
    { label: isEnglish ? 'Warping' : '反り', path: `${prefix}/troubleshooting/warping`, icon: 'ri-arrow-up-line' },
    { label: isEnglish ? 'Stringing' : '糸引き', path: `${prefix}/troubleshooting/stringing`, icon: 'ri-links-line' },
    { label: isEnglish ? 'Layer Adhesion' : '層間剥離', path: `${prefix}/troubleshooting/delamination`, icon: 'ri-stack-line' },
    { label: isEnglish ? 'Sagging' : '滲み', path: `${prefix}/troubleshooting/bleeding`, icon: 'ri-drop-line' },
    { label: isEnglish ? 'Accuracy' : '寸法精度', path: `${prefix}/troubleshooting/accuracy`, icon: 'ri-ruler-line' }
  ];

  const troubleCategories = [
    {
      title: TEXT.warping,
      description: TEXT.warpingDesc,
      causes: TEXT.warpingCauses,
      solutions: TEXT.warpingSolutions,
      severity: 'high',
      slug: 'warping'
    },
    {
      title: TEXT.stringing,
      description: TEXT.stringingDesc,
      causes: TEXT.stringingCauses,
      solutions: TEXT.stringSolutions,
      severity: 'medium',
      slug: 'stringing'
    },
    {
      title: TEXT.delamination,
      description: TEXT.delaminationDesc,
      causes: TEXT.delaminationCauses,
      solutions: TEXT.delaminationSolutions,
      severity: 'high',
      slug: 'delamination'
    },
    {
      title: TEXT.bleeding,
      description: TEXT.bleedingDesc,
      causes: TEXT.bleedingCauses,
      solutions: TEXT.bleedingSolutions,
      severity: 'medium',
      slug: 'bleeding'
    },
    {
      title: TEXT.accuracy,
      description: TEXT.accuracyDesc,
      causes: TEXT.accuracyCauses,
      solutions: TEXT.accuracySolutions,
      severity: 'low',
      slug: 'accuracy'
    },
    {
      title: TEXT.support,
      description: TEXT.supportDesc,
      causes: TEXT.supportCauses,
      solutions: TEXT.supportSolutions,
      severity: 'low',
      slug: 'support'
    }
  ];

  const getSeverityClass = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'border-l-4 border-l-[#b56b3c]';
      case 'medium':
        return 'border-l-4 border-l-[#4c7ca6]';
      case 'low':
        return 'border-l-4 border-l-[#5f8d63]';
      default:
        return '';
    }
  };

  const getSeverityLabel = (severity: string) => {
    switch (severity) {
      case 'high':
        return TEXT.severityHigh;
      case 'medium':
        return TEXT.severityMedium;
      case 'low':
        return TEXT.severityLow;
      default:
        return '';
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-graphite mechanical-grid">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="space-y-8 mt-8">
            {/* Page Header */}
            <div className="text-center space-y-4 mt-8">
              <h1 className="text-4xl font-bold text-white font-['Exo']">
                {TEXT.pageTitle}
              </h1>
              <p className="text-metal-light text-lg font-['Roboto_Mono']">
                {TEXT.pageSubtitle}
              </p>
              <div className="section-divider max-w-md mx-auto"></div>
            </div>

            {/* Trouble Categories */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {troubleCategories.map((trouble, index) => (
                <div
                  key={index}
                  className={`clickable-card p-6 ${getSeverityClass(trouble.severity)}`}
                >
                  <div className="space-y-4">
                    <div className="flex items-start justify-between">
                      <Link
                        to={`${prefix}/troubleshooting/${trouble.slug}`}
                        className="text-xl font-semibold text-white font-['Exo'] hover:text-accent hover:underline transition-colors"
                      >
                        {trouble.title}
                      </Link>
                      <span className="tech-label text-xs">
                        {getSeverityLabel(trouble.severity)}
                      </span>
                    </div>
                    
                    <p className="text-metal-light text-sm">
                      {trouble.description}
                    </p>

                    <div className="space-y-3">
                      <div>
                        <h4 className="text-accent text-sm font-semibold font-['Exo'] mb-1">
                          {TEXT.causesLabel}
                        </h4>
                        <ul className="text-metal-light text-xs space-y-1">
                          {trouble.causes.map((cause, idx) => (
                            <li key={idx} className="flex items-center space-x-2">
                              <i className="ri-arrow-right-s-line text-accent"></i>
                              <span>{cause}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <h4 className="text-accent text-sm font-semibold font-['Exo'] mb-1">
                          {TEXT.solutionsLabel}
                        </h4>
                        <ul className="text-metal-light text-xs space-y-1">
                          {trouble.solutions.map((solution, idx) => (
                            <li key={idx} className="flex items-center space-x-2">
                              <i className="ri-check-line text-[#5f8d63]"></i>
                              <span>{solution}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="pt-2">
                      <Link
                        to={`${prefix}/troubleshooting/${trouble.slug}`}
                        className="flex items-center text-accent text-sm font-['Roboto_Mono'] hover:text-white transition-colors"
                      >
                        <span>{TEXT.detailsLink}</span>
                        <i className="ri-arrow-right-line ml-2"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Notes */}
            <div className="static-card p-6">
              <h3 className="text-lg font-semibold text-white font-['Exo'] mb-4">
                {TEXT.notesTitle}
              </h3>
              <div className="space-y-2 text-metal-light text-sm">
                <p>{TEXT.note1}</p>
                <p>{TEXT.note2}</p>
                <p>{TEXT.note3}</p>
                <p>{TEXT.note4}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default TroubleshootingPage;
